import { type FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import { useGeoCoderLocations } from '@/entities/geoCoder';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries, @direct-frontend/modules/entry-point -- Нужно поправить
import { type PickupAddressFieldValue } from '@/features/OrderForm/model/forms/fieldPickupAddress';
import { type MapSubmitButtonComponent } from '@/shared/api/map';
import { type OrderDto } from '@/shared/api/order';
import { Dialog, DialogContent, DialogTitle } from '@/shared/components/Dialog';
import { buildLocation } from '@/shared/lib/buildLocation';
import { PickupAddressProvider } from '../../PickupAddressContext';
import { type PickupAddressValue } from '../../types/PickupAddressValue';
import { PickupAddressFilters } from '../PickupAddressFilters';
import { PickupAddressMap } from '../PickupAddressMap';
import { PickupAddressSidebar } from '../PickupAddressSiderbar';
import styles from './PickupNewAddressDialog.module.css';

export interface PickupNewAddressDialogProps {
    order: OrderDto;
    storeSlug: string;
    defaultLocation: string;
    value?: PickupAddressFieldValue;
    isOpen: boolean;
    onClose: VoidFunction;
    onChange?: (value: PickupAddressValue) => void;
    reinitializeOrderForm: (initialOrder: OrderDto, updatedOrder: OrderDto) => void;
    submitButton: MapSubmitButtonComponent;
}

export interface RefHandler {
    reset: VoidFunction;
}

export const PickupNewAddressDialog: FunctionComponent<PickupNewAddressDialogProps> = (props) => {
    const { order, storeSlug, value, defaultLocation, isOpen, onChange, onClose, submitButton, reinitializeOrderForm } =
        props;

    const [searchQuery, setSearchQuery] = useState(value?.address ? buildLocation(value) : defaultLocation);
    const debounceQuery = useDebouncedValue(searchQuery, 300);

    const pickupProviderRef = useRef<RefHandler>();

    const [loadGeoLocation, { data: locations = [] }] = useGeoCoderLocations();

    const handleChange = useCallback(
        (value: PickupAddressValue) => {
            setSearchQuery(value?.address ? buildLocation(value) : defaultLocation);
            onChange?.(value);
            onClose();
        },
        [defaultLocation, onChange, onClose]
    );

    useEffect(() => {
        if (debounceQuery) {
            loadGeoLocation({ geocode: debounceQuery });
        }
    }, [loadGeoLocation, debounceQuery]);

    const handleClose = useCallback(() => {
        onClose();
        setSearchQuery(value?.address ? buildLocation(value) : defaultLocation);
        pickupProviderRef.current?.reset();
    }, [defaultLocation, onClose, value]);

    return (
        <PickupAddressProvider
            pickupId={value?.pickupPointId}
            storeSlug={storeSlug}
            orderId={order.id}
            serviceType={value?.serviceType}
            type="pickup"
            ref={pickupProviderRef}
        >
            <Dialog
                isFullscreen
                containerClassName={styles.modalContainer}
                className={styles.modal}
                isOpen={isOpen}
                onClose={handleClose}
            >
                <DialogTitle title="Выберите пункт выдачи" />
                <DialogContent className={styles.content}>
                    <PickupAddressSidebar
                        title="Выберите пункт выдачи"
                        filtersSlot={<PickupAddressFilters />}
                        order={order}
                        reinitializeOrderForm={reinitializeOrderForm}
                        storeSlug={storeSlug}
                        className={styles.sidebar}
                        inputValue={searchQuery}
                        onInputChange={setSearchQuery}
                        onChange={handleChange}
                        submitButton={submitButton}
                    />
                    <div className={styles.mapWrapper}>
                        <div className={styles.map}>
                            <PickupAddressMap locations={locations} />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </PickupAddressProvider>
    );
};
